/**
 * PersonalityStep — Código Interno (Eneagrama).
 *
 * Optional step. User self-selects their dominant type (1-9) and an optional
 * adjacent wing. Source defaults to 'self_select'. Validity is always true
 * (skippable) — the field is opt-in. The actual data persists into
 * `user_business_context.enneagram_type / enneagram_wing / enneagram_source`.
 */

import { useEffect, useRef, useState } from "react";
import { StepContainer } from "../components/StepContainer";
import {
  PersonalityFields,
  type PersonalityValue,
} from "../components/fields/PersonalityFields";
import { useUpdateBusinessContext } from "../hooks/useUpdateBusinessContext";
import { useBusinessContext } from "@/hooks/useBusinessContext";
import { findStepIndexBySlug, STEPS } from "../config/steps";
import type { EnneagramTypeId } from "@/types/supabase";

const EMPTY: PersonalityValue = {
  enneagram_type: null,
  enneagram_wing: null,
  enneagram_source: null,
};

export interface PersonalityStepProps {
  onValidityChange: (valid: boolean) => void;
  onAttemptContinue: (continueFn: () => Promise<boolean>) => void;
  onMissingChange?: (labels: string[]) => void;
}

export function PersonalityStep({
  onValidityChange,
  onAttemptContinue,
  onMissingChange,
}: PersonalityStepProps) {
  const stepIdx = findStepIndexBySlug("codigo-interno");
  const step = STEPS[stepIdx];
  const { mutate, mutateDebounced } = useUpdateBusinessContext();
  const { query } = useBusinessContext();

  const [values, setValues] = useState<PersonalityValue>(EMPTY);
  const valuesRef = useRef(values);
  valuesRef.current = values;

  useEffect(() => {
    const row = query.data;
    if (!row) return;
    setValues({
      enneagram_type: (row.enneagram_type as EnneagramTypeId | null) ?? null,
      enneagram_wing: (row.enneagram_wing as EnneagramTypeId | null) ?? null,
      enneagram_source: row.enneagram_source ?? null,
    });
  }, [query.data]);

  // Step is always valid — skipping is allowed.
  useEffect(() => {
    onValidityChange(true);
    onMissingChange?.([]);
  }, [onValidityChange, onMissingChange]);

  useEffect(() => {
    onAttemptContinue(async () => {
      await mutate(valuesRef.current);
      return true;
    });
  }, [mutate, onAttemptContinue]);

  const handleChange = (next: PersonalityValue) => {
    setValues(next);
    mutateDebounced(next);
  };

  return (
    <StepContainer
      icon={step.icon}
      headline={step.headline}
      sub={step.sub}
      importanceNote="Pode pular se não souber. Quanto mais o sistema souber, melhor calibra feedback e copy ao seu estilo."
    >
      <PersonalityFields value={values} onChange={handleChange} />
    </StepContainer>
  );
}
